import React from 'react';
import moment from 'moment';
import { ExportLicense } from 'api/ExportHubAPI';
import { formatCurrency } from './licensePresets';

interface Props {
  /** The licences linked to this product. */
  licenses: ExportLicense[];
}

/**
 * Statuses that actually authorize an export, out of the License status enum
 * ('Requested' | 'Active' | 'Expiring' | 'Expired' | 'Denied' | 'Closed').
 *
 * 'Requested' is an application still in flight — it authorizes nothing, and
 * its figure lives in proposed_value, not value. 'Expiring' is still valid, so
 * it counts. The three terminal states are spent.
 */
const AUTHORIZING_STATUSES = ['Active', 'Expiring'];

/**
 * A license can carry an authorizing status while its expiry date has already
 * passed (the nightly status sweep hasn't caught it yet), so check the date as
 * well — a lapsed license must not inflate the value or count.
 */
const isUsable = (l: ExportLicense): boolean => {
  if (!AUTHORIZING_STATUSES.includes(l.status)) return false;
  if (!l.expiry_date) return true;
  return moment(l.expiry_date).isSameOrAfter(moment().startOf('day'), 'day');
};

const Tile: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: 'default' | 'success' | 'warning';
}> = ({ label, value, sub, tone = 'default' }) => (
  <div className='eh-lsum-tile'>
    <span className='eh-lsum-label'>{label}</span>
    <span className={`eh-lsum-value ${tone}`}>{value}</span>
    {!!sub && <span className='eh-lsum-sub'>{sub}</span>}
  </div>
);

/**
 * Roll-up of the licences linked to one product: how many are usable, what they
 * authorize in total, and — the headline — how much value is left across them.
 * The per-licence rows underneath (Linked Licenses) carry the detail.
 */
const LicenseSummary: React.FC<Props> = ({ licenses }) => {
  const active = licenses.filter(isUsable);

  // Only licences with a recorded ceiling contribute to the value figures; a
  // licence still awaiting approval has a proposed value, not an authorized one.
  const valued = active.filter((l) => l.value !== null);
  const authorized = valued.reduce((n, l) => n + (l.value as number), 0);
  const used = valued.reduce((n, l) => n + (l.value_used || 0), 0);
  const remaining = Math.max(authorized - used, 0);
  const allocated = active.reduce((n, l) => n + (l.allocated_value ?? 0), 0);
  const pctUsed = authorized > 0 ? used / authorized : null;

  const nextExpiry = active
    .filter((l) => !!l.expiry_date)
    .sort((a, b) => +new Date(a.expiry_date as string) - +new Date(b.expiry_date as string))[0];
  const daysLeft = nextExpiry
    ? moment(nextExpiry.expiry_date).diff(moment(), 'days')
    : null;

  const remainingTone =
    pctUsed === null ? 'default' : pctUsed >= 0.9 ? 'warning' : 'success';

  return (
    <div className='eh-panel eh-lsum'>
      <div className='eh-panel-title eh-panel-title-flex'>
        <span>License Summary</span>
        {valued.length < active.length && (
          <span className='eh-panel-note'>
            {active.length - valued.length} of {active.length} without a value
            ceiling
          </span>
        )}
      </div>

      {licenses.length === 0 ? (
        <div className='eh-detail-note'>
          No licences linked — nothing authorizes this product for export yet.
        </div>
      ) : (
        <>
          <div className='eh-lsum-grid'>
            <Tile
              label='Linked licenses'
              value={licenses.length}
              sub={
                active.length === licenses.length
                  ? 'all usable'
                  : `${active.length} usable`
              }
            />
            <Tile
              label='Value left'
              value={valued.length ? formatCurrency(remaining) : '—'}
              sub={
                valued.length
                  ? `of ${formatCurrency(authorized)} authorized`
                  : 'no value ceilings recorded'
              }
              tone={remainingTone}
            />
            <Tile
              label='Value used'
              value={valued.length ? formatCurrency(used) : '—'}
              sub={
                pctUsed !== null
                  ? `${Math.round(pctUsed * 100)}% of authorized`
                  : undefined
              }
            />
            <Tile
              label='Allocated to this product'
              value={allocated > 0 ? formatCurrency(allocated) : '—'}
              sub={
                allocated > 0
                  ? 'planning ceiling across licenses'
                  : 'no allocation set'
              }
            />
            <Tile
              label='Next expiry'
              value={
                nextExpiry
                  ? moment(nextExpiry.expiry_date).format('MM/DD/YYYY')
                  : '—'
              }
              sub={
                daysLeft === null
                  ? 'no expiry dates'
                  : daysLeft < 0
                  ? 'expired'
                  : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
              }
              tone={daysLeft !== null && daysLeft <= 30 ? 'warning' : 'default'}
            />
          </div>

          {pctUsed !== null && (
            <div className='eh-lsum-bar-wrap'>
              <span className='eh-lsum-bar'>
                <span
                  className={`eh-lsum-bar-fill ${
                    pctUsed >= 0.9 ? 'warning' : ''
                  }`}
                  style={{ width: `${Math.min(pctUsed, 1) * 100}%` }}
                />
              </span>
              <span className='eh-lsum-bar-label'>
                {formatCurrency(remaining)} left across {valued.length} license
                {valued.length === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LicenseSummary;
